import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import "./AdminMessaging.css";
import { loadHouseholds } from "../components/communications/loadHouseholds";
import { loadPrograms } from "../components/communications/loadPrograms";
import { scheduleTime } from "../components/communications/scheduleTime";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import AdminLayout from "../components/AdminLayout";
import { trackAdminWorkflowCompleted } from "../utils/adminPortalRollout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import RichTextEditor from "../components/RichTextEditor";
import ProgramDescription from "../components/public-org/ProgramDescription";
import {
  MessageSquare, Send, Users, User, Mail, Search, Plus,
  Clock, CheckCircle, AlertCircle, Loader2, Trophy, Eye, ArrowLeft,
  FileText, Save, Trash2, Edit, Copy, Sparkles, Wand2,
  CalendarClock, BarChart3, XCircle, Calendar, Activity, ShieldCheck,
  Megaphone, ArrowRight, X
} from "lucide-react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  describeAudience,
  describeMessageStatus,
  getCommunicationActions,
  getDeliverySummary,
  getRecoverableDeliveries,
} from "../components/communications/communicationOperations";

// Default email templates
const DEFAULT_TEMPLATES = [
  {
    id: "welcome",
    name: "Welcome Message",
    category: "general",
    subject: "Welcome to {{program_name}}!",
    body: "Dear {{parent_name}},\n\nWelcome to {{program_name}}! We're excited to have {{player_name}} join our program.\n\nHere are a few things to know:\n• First practice: [DATE]\n• Location: [LOCATION]\n• What to bring: [ITEMS]\n\nIf you have any questions, please don't hesitate to reach out.\n\nBest regards,\n{{organization_name}}"
  },
  {
    id: "schedule_change",
    name: "Schedule Change",
    category: "scheduling",
    subject: "Schedule Update - {{program_name}}",
    body: "Dear Parents,\n\nThis is to inform you of a schedule change for {{program_name}}.\n\n[DESCRIBE CHANGES HERE]\n\nWe apologize for any inconvenience this may cause. Please update your calendars accordingly.\n\nThank you for your understanding.\n\nBest regards,\n{{organization_name}}"
  },
  {
    id: "practice_reminder",
    name: "Practice Reminder",
    category: "scheduling",
    subject: "Reminder: Practice Tomorrow - {{program_name}}",
    body: "Hi {{parent_name}},\n\nThis is a friendly reminder that {{player_name}} has practice tomorrow.\n\n📅 Date: [DATE]\n⏰ Time: [TIME]\n📍 Location: [LOCATION]\n\nPlease ensure your child arrives 10-15 minutes early and brings:\n• Water bottle\n• Appropriate gear\n\nSee you there!\n\n{{organization_name}}"
  },
  {
    id: "game_reminder",
    name: "Game Day Reminder",
    category: "scheduling",
    subject: "Game Day Tomorrow! - {{program_name}}",
    body: "Hi {{parent_name}},\n\nGet ready! {{player_name}} has a game tomorrow!\n\n🏆 Opponent: [OPPONENT]\n📅 Date: [DATE]\n⏰ Arrival Time: [TIME]\n📍 Location: [LOCATION]\n\nPlayers should arrive 30 minutes before game time for warm-ups.\n\nGo team! 🎉\n\n{{organization_name}}"
  },
  {
    id: "payment_reminder",
    name: "Payment Reminder",
    category: "financial",
    subject: "Payment Reminder - {{program_name}}",
    body: "Dear {{parent_name}},\n\nThis is a friendly reminder that payment is due for {{player_name}}'s registration in {{program_name}}.\n\nAmount Due: $[AMOUNT]\nDue Date: [DATE]\n\nYou can make your payment by logging into your account at [LINK].\n\nIf you have any questions about your balance, please contact us.\n\nThank you,\n{{organization_name}}"
  },
  {
    id: "season_end",
    name: "End of Season",
    category: "general",
    subject: "Thank You! - {{program_name}} Season Wrap-up",
    body: "Dear {{parent_name}},\n\nAs the {{program_name}} season comes to an end, we want to thank you for being part of our program!\n\nIt's been a wonderful season watching {{player_name}} grow and develop. We hope they had as much fun as we did.\n\n[ADD SEASON HIGHLIGHTS]\n\nWe hope to see you next season!\n\nWith gratitude,\n{{organization_name}}"
  },
  {
    id: "weather_cancellation",
    name: "Weather Cancellation",
    category: "scheduling",
    subject: "⚠️ CANCELLED: Today's {{program_name}} Activity",
    body: "Dear Parents,\n\nDue to inclement weather conditions, today's {{program_name}} activity has been CANCELLED.\n\nSafety is our top priority, and we've determined that conditions are not suitable for outdoor activities.\n\n[MAKEUP INFORMATION IF APPLICABLE]\n\nPlease stay safe and we'll see you at the next scheduled event.\n\n{{organization_name}}"
  },
  // Tournament Templates
  {
    id: "tournament_registration_open",
    name: "🏆 Tournament Registration Open",
    category: "tournament",
    subject: "Registration Now Open: {{tournament_name}}",
    body: "Dear Team Managers,\n\n🏆 We're excited to announce that registration is now OPEN for {{tournament_name}}!\n\n📅 Tournament Date: [DATE]\n📍 Location: [LOCATION]\n💰 Entry Fee: $[AMOUNT]\n👥 Team Spots: Limited to [NUMBER] teams\n\n⏰ Registration Deadline: [DEADLINE]\n\nWhat to Expect:\n• [FORMAT] format tournament\n• [GAME GUARANTEE] game guarantee\n• Trophies/medals for top finishers\n• Concessions available on-site\n\n🔗 Register your team now: [REGISTRATION_LINK]\n\nDon't miss out – spots fill up fast!\n\nBest regards,\n{{organization_name}}"
  },
  {
    id: "tournament_registration_closing",
    name: "🚨 Tournament Registration Closing Soon",
    category: "tournament",
    subject: "Last Chance! Registration Closes Soon - {{tournament_name}}",
    body: "Dear Team Managers,\n\n⏰ TIME IS RUNNING OUT!\n\nRegistration for {{tournament_name}} closes in [DAYS] days!\n\n📅 Registration Deadline: [DEADLINE]\n👥 Spots Remaining: [NUMBER]\n\nIf your team hasn't registered yet, now is the time to secure your spot.\n\n🔗 Register Now: [REGISTRATION_LINK]\n\nDon't let your team miss this opportunity!\n\nBest regards,\n{{organization_name}}"
  },
  {
    id: "tournament_registration_confirmed",
    name: "✅ Tournament Registration Confirmed",
    category: "tournament",
    subject: "Welcome to {{tournament_name}}! Registration Confirmed",
    body: "Dear {{team_name}} Team Manager,\n\n🎉 Congratulations! Your registration for {{tournament_name}} has been confirmed!\n\n📋 Registration Details:\n• Team: {{team_name}}\n• Division: [DIVISION]\n• Entry Fee: $[AMOUNT] - PAID ✓\n\n📅 Tournament Date: [DATE]\n📍 Location: [LOCATION]\n⏰ Check-in Time: [TIME]\n\nNext Steps:\n1. Submit your final roster by [ROSTER_DEADLINE]\n2. Complete any required player waivers\n3. Watch for the bracket release email\n\nWe're excited to have your team compete!\n\n{{organization_name}}"
  },
  {
    id: "tournament_bracket_released",
    name: "📊 Tournament Bracket Released",
    category: "tournament",
    subject: "🏆 Bracket Released - {{tournament_name}}",
    body: "Dear Team Managers,\n\n📊 THE BRACKET IS HERE!\n\nThe official bracket for {{tournament_name}} has been released!\n\n🔗 View the Full Bracket: [BRACKET_LINK]\n\n📋 Your Team's First Game:\n• 🆚 Opponent: [OPPONENT]\n• 📅 Date: [DATE]\n• ⏰ Game Time: [TIME]\n• 📍 Field/Court: [FIELD]\n\n⚠️ Important Reminders:\n• Arrive 30 minutes before your game time\n• Check in at the tournament tent upon arrival\n• Bring player ID cards/roster\n• Have your team ready for warm-ups\n\nGood luck! Let the games begin! 🎉\n\n{{organization_name}}"
  },
  {
    id: "tournament_schedule_update",
    name: "🔄 Tournament Schedule Update",
    category: "tournament",
    subject: "Schedule Update - {{tournament_name}}",
    body: "Dear Team Managers,\n\n📢 IMPORTANT SCHEDULE UPDATE\n\nThere has been a change to the tournament schedule for {{tournament_name}}.\n\n❌ Previous:\n[OLD SCHEDULE DETAILS]\n\n✅ New:\n[NEW SCHEDULE DETAILS]\n\n📋 Your Updated Game:\n• 🆚 Opponent: [OPPONENT]\n• 📅 Date: [DATE]\n• ⏰ New Time: [TIME]\n• 📍 Field/Court: [FIELD]\n\nPlease update your calendars accordingly.\n\n🔗 View Updated Bracket: [BRACKET_LINK]\n\nWe apologize for any inconvenience.\n\n{{organization_name}}"
  },
  {
    id: "tournament_game_reminder",
    name: "⚽ Tournament Game Reminder",
    category: "tournament",
    subject: "Game Tomorrow! {{tournament_name}}",
    body: "Dear {{team_name}},\n\n🏆 TOURNAMENT GAME TOMORROW!\n\nYour team has a game in {{tournament_name}} tomorrow!\n\n📋 Game Details:\n• 🆚 Opponent: [OPPONENT]\n• 📅 Date: [DATE]\n• ⏰ Game Time: [TIME]\n• 📍 Field/Court: [FIELD]\n• Round: [ROUND - e.g., Quarterfinal]\n\n⏰ Arrival: Please arrive 30 minutes early for check-in and warm-ups.\n\n📝 Bring:\n• Player ID cards\n• Water bottles\n• Team uniforms\n• Positive attitude!\n\nGo get 'em! 💪\n\n{{organization_name}}"
  },
  {
    id: "tournament_results",
    name: "🏅 Tournament Results & Recap",
    category: "tournament",
    subject: "Tournament Results - {{tournament_name}}",
    body: "Dear Teams,\n\n🏆 {{tournament_name}} is in the books!\n\nThank you all for an incredible tournament! Here are the final results:\n\n🥇 1st Place: [CHAMPION]\n🥈 2nd Place: [RUNNER_UP]\n🥉 3rd Place: [THIRD_PLACE]\n\n📊 Tournament Stats:\n• Total Games Played: [NUMBER]\n• Total Goals/Points: [NUMBER]\n• MVP Award: [PLAYER_NAME] - [TEAM]\n\nCongratulations to all teams who competed! Your hard work, sportsmanship, and team spirit made this tournament a success.\n\n📸 Photos: [PHOTO_LINK]\n🔗 Full Results: [RESULTS_LINK]\n\nWe hope to see you at our next tournament!\n\n{{organization_name}}"
  },
  {
    id: "tournament_weather_delay",
    name: "⛈️ Tournament Weather Delay",
    category: "tournament",
    subject: "⚠️ Weather Delay - {{tournament_name}}",
    body: "Dear Team Managers,\n\n⛈️ WEATHER DELAY ALERT\n\nDue to [WEATHER CONDITIONS], {{tournament_name}} games are currently DELAYED.\n\n⏰ Current Status: Games on hold as of [TIME]\n📍 Affected Fields: [FIELDS]\n\n🔄 What to Do:\n• Stay in your vehicles or the covered area\n• Keep your team together\n• Monitor your phone for updates\n• We will text/email when play resumes\n\n⏱️ Expected Resume Time: [ESTIMATED TIME] (subject to conditions)\n\nSafety is our top priority. We will provide updates every 30 minutes.\n\nStay safe and dry!\n\n{{organization_name}}"
  }
];

const TEMPLATE_CATEGORIES = [
  { value: "all", label: "All Templates" },
  { value: "general", label: "General" },
  { value: "scheduling", label: "Scheduling" },
  { value: "financial", label: "Financial" },
  { value: "tournament", label: "🏆 Tournament" }
];

const EmbeddedLayout = ({ children }) => <>{children}</>;

// The shared editor does not forward accessibility props. Name its actual
// editable node locally, including Tiptap's deferred mount, without replacing it.
const MessageEditor = ({ children }) => {
  const root = useRef(null);
  useLayoutEffect(() => {
    const nameEditor = () => {
      const editor = root.current?.querySelector('[contenteditable="true"]');
      if (!editor) return;
      editor.setAttribute('role', 'textbox');
      editor.setAttribute('aria-label', 'Message');
      editor.setAttribute('aria-multiline', 'true');
    };
    nameEditor();
    const observer = new MutationObserver(nameEditor);
    observer.observe(root.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return <div ref={root} className="announcement-editor" role="group" aria-labelledby="announcement-message-label">{children}</div>;
};

const AdminMessaging = ({ embedded = false }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [households, setHouseholds] = useState([]);
  const dataRequest = useRef(0);
  const dataToken = useRef(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composerExpanded, setComposerExpanded] = useState(false);
  const composerRef = useRef(null);
  const expandRef = useRef(null);
  const wasExpanded = useRef(false);

  useLayoutEffect(() => {
    if (!composeOpen || !composerExpanded) {
      if (wasExpanded.current) expandRef.current?.focus({ preventScroll: true });
      wasExpanded.current = false;
      return;
    }
    wasExpanded.current = true;
    expandRef.current?.focus();
    const restore = [];
    let branch = composerRef.current;
    while (branch && branch !== document.body) {
      for (const sibling of branch.parentElement?.children || []) {
        if (sibling === branch || !(sibling instanceof HTMLElement)) continue;
        const inert = sibling.getAttribute('inert');
        const hidden = sibling.getAttribute('aria-hidden');
        sibling.setAttribute('inert', '');
        sibling.setAttribute('aria-hidden', 'true');
        restore.push(() => {
          if (inert === null) sibling.removeAttribute('inert'); else sibling.setAttribute('inert', inert);
          if (hidden === null) sibling.removeAttribute('aria-hidden'); else sibling.setAttribute('aria-hidden', hidden);
        });
      }
      branch = branch.parentElement;
    }
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const childEvents = new WeakSet();
    const capture = event => {
      if (document.querySelector('[role="dialog"], [role="alertdialog"], [role="listbox"], [role="menu"], [data-radix-popper-content-wrapper]')) childEvents.add(event);
    };
    const handleKey = event => {
      if (childEvents.has(event) || event.defaultPrevented || event.isComposing) return;
      if (event.key === 'Escape' && !event.repeat) {
        event.preventDefault();
        setComposerExpanded(false);
      }
      if (event.key === 'Tab') {
        const controls = [...composerRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), [contenteditable="true"], [tabindex="0"]')].filter(el => el.getAttribute('aria-hidden') !== 'true');
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    window.addEventListener('keydown', capture, true);
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', capture, true);
      window.removeEventListener('keydown', handleKey);
      restore.reverse().forEach(reset => reset());
      document.body.style.overflow = overflow;
    };
  }, [composeOpen, composerExpanded]);
  const [previewMode, setPreviewMode] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [recipientCount, setRecipientCount] = useState(0);
  const [audienceState, setAudienceState] = useState("idle");
  const audienceRequest = useRef(0);
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [suppressedCount, setSuppressedCount] = useState(0);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const schedule = scheduleTime(scheduledAt);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const scheduledRequest = useRef(0);
  const analyticsRequest = useRef(0);
  // A render closure belongs to one committed organization/token lifecycle.
  // Identity (not token equality) prevents A-B-A continuations reacquiring reads.
  const lifecycle = useMemo(() => ({}), [token, user?.org_id]);
  const activeLifecycle = useRef(null);
  const ownsLifecycle = () => activeLifecycle.current === lifecycle;
  
  const [messageForm, setMessageForm] = useState({
    type: "broadcast",
    program_id: "",
    household_id: "",
    subject: "",
    body: ""
  });

  const [templateForm, setTemplateForm] = useState({
    name: "",
    category: "general",
    subject: "",
    body: ""
  });

  useLayoutEffect(() => {
    activeLifecycle.current = lifecycle;
    setSending(false);
    setSavingTemplate(false);
    setAiLoading(false);
    dataToken.current = null;
    dataRequest.current += 1;
    audienceRequest.current += 1;
    scheduledRequest.current += 1;
    analyticsRequest.current += 1;
    setScheduledEmails([]);
    setAnalytics(null);
    setScheduledLoading(false);
    setAnalyticsLoading(false);
    setLoading(true);
    setHouseholds([]);
    setPrograms([]);
    setComposeOpen(false);
    setComposerExpanded(false);
    setPreviewMode(false);
    setMessageForm(form => ({ ...form, program_id: "", household_id: "" }));
    return () => {
      activeLifecycle.current = null;
      dataRequest.current += 1;
      audienceRequest.current += 1;
      scheduledRequest.current += 1;
      analyticsRequest.current += 1;
    };
  }, [token, user?.org_id, lifecycle]);

  useEffect(() => {
    fetchData();
  }, [token, user?.org_id, lifecycle]);

  useEffect(() => {
    if (dataToken.current !== token || loading) return;
    if (messageForm.type === "broadcast" && messageForm.program_id) {
      fetchAudiencePreview(messageForm);
    } else if (messageForm.type === "individual" && messageForm.household_id) {
      fetchAudiencePreview(messageForm);
    } else {
      audienceRequest.current += 1;
      setAudienceState("idle");
      setRecipientCount(0);
      setRecipientEmails([]);
      setSuppressedCount(0);
    }
    return () => { audienceRequest.current += 1; };
  }, [messageForm.type, messageForm.program_id, messageForm.household_id, token, loading]);

  const fetchData = async () => {
    if (!ownsLifecycle()) return;
    const request = ++dataRequest.current;
    // Bootstrap and tab refresh share ownership of the scheduled rows.
    const scheduled = ++scheduledRequest.current;
    setLoading(true);
    setLoadError(false);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [messagesRes, programsRes, householdsRes, templatesRes, scheduledRes] = await Promise.all([
        axios.get(`${API}/admin/messages`, { headers }),
        loadPrograms(async page => {
          if (request !== dataRequest.current) throw new Error("Obsolete program request");
          const response = await axios.get(`${API}/programs`, { headers, params: { page, page_size: 100 } });
          if (request !== dataRequest.current) throw new Error("Obsolete program request");
          return response.data;
        }),
        loadHouseholds(async page => {
          if (request !== dataRequest.current) throw new Error("Obsolete household request");
          const response = await axios.get(`${API}/admin/households`, { headers, params: { page, page_size: 100 } });
          if (request !== dataRequest.current) throw new Error("Obsolete household request");
          return response.data;
        }),
        axios.get(`${API}/admin/email-templates`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/communications/scheduled?status=all`, { headers }).catch(() => ({ data: [] }))
      ]);
      if (request !== dataRequest.current) return;
      dataToken.current = token;
      setMessages(messagesRes.data);
      setPrograms(programsRes);
      setHouseholds(householdsRes);
      // Merge default templates with custom ones
      const customTemplates = templatesRes.data || [];
      setTemplates([...DEFAULT_TEMPLATES, ...customTemplates.filter(t => !DEFAULT_TEMPLATES.find(d => d.id === t.id))]);
      if (scheduled === scheduledRequest.current) setScheduledEmails(scheduledRes.data || []);
    } catch (error) {
      if (request !== dataRequest.current) return;
      console.error("Error fetching data:", error);
      setHouseholds([]);
      setPrograms([]);
      setLoadError(true);
    } finally {
      if (request === dataRequest.current) setLoading(false);
      if (scheduled === scheduledRequest.current) setScheduledLoading(false);
    }
  };

  const fetchAudiencePreview = async (audience) => {
    if (!ownsLifecycle()) return;
    const request = ++audienceRequest.current;
    setAudienceState("loading");
    setRecipientCount(0);
    setRecipientEmails([]);
    setSuppressedCount(0);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/admin/messages/preview`, audience, { headers });
      if (request !== audienceRequest.current) return;
      setAudienceState("ready");
      setRecipientCount(response.data.recipient_count || 0);
      setRecipientEmails(response.data.sample_emails || []);
      setSuppressedCount(response.data.suppressed_count || 0);
    } catch (error) {
      if (request !== audienceRequest.current) return;
      setAudienceState("error");
      console.error("Error fetching audience preview:", error);
      setRecipientCount(0);
      setRecipientEmails([]);
      setSuppressedCount(0);
    }
  };

  const fetchScheduledEmails = async () => {
    if (!ownsLifecycle()) return;
    const request = ++scheduledRequest.current;
    setScheduledLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/communications/scheduled?status=all`, { headers });
      if (request !== scheduledRequest.current) return;
      setScheduledEmails(res.data || []);
    } catch (error) {
      if (request !== scheduledRequest.current) return;
      console.error("Error fetching scheduled emails:", error);
    } finally {
      if (request === scheduledRequest.current) setScheduledLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    if (!ownsLifecycle()) return;
    const request = ++analyticsRequest.current;
    setAnalyticsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/communications/analytics`, { headers });
      if (request !== analyticsRequest.current) return;
      setAnalytics(res.data);
    } catch (error) {
      if (request !== analyticsRequest.current) return;
      console.error("Error fetching analytics:", error);
    } finally {
      if (request === analyticsRequest.current) setAnalyticsLoading(false);
    }
  };

  const handleCancelScheduled = async (scheduleId) => {
    if (!ownsLifecycle()) return;
    if (!confirm("Cancel this scheduled email?")) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/communications/scheduled/${scheduleId}`, { headers });
      if (!ownsLifecycle()) return;
      toast.success("Scheduled email cancelled");
      fetchScheduledEmails();
    } catch (error) {
      if (!ownsLifecycle()) return;
      toast.error("Failed to cancel scheduled email");
    }
  };

  const handleScheduleEmail = async () => {
    if (!ownsLifecycle()) return;
    const selectedTime = scheduleTime(scheduledAt);
    if (selectedTime.error) { toast.error(selectedTime.error); return; }
    setSending(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/communications/schedule`, {
        ...messageForm,
        scheduled_at: selectedTime.utc,
      }, { headers });
      if (!ownsLifecycle()) return;
      const displayDate = scheduledAt ? format(new Date(scheduledAt), "MMM d, h:mm a") : "";
      toast.success(`Email scheduled for ${displayDate}`);
      setComposeOpen(false);
      setComposerExpanded(false);
      setPreviewMode(false);
      setScheduleMode(false);
      setScheduledAt("");
      setMessageForm({ type: "broadcast", program_id: "", household_id: "", subject: "", body: "" });
      fetchScheduledEmails();
    } catch (error) {
      if (!ownsLifecycle()) return;
      console.error("Error scheduling email:", error);
      toast.error(error.response?.data?.detail || "Failed to schedule email");
    } finally {
      if (ownsLifecycle()) setSending(false);
    }
  };

  // Fetch scheduled/analytics when switching tabs
  useEffect(() => {
    if (activeTab === "scheduled") fetchScheduledEmails();
    if (activeTab === "analytics") fetchAnalytics();
  }, [activeTab, token, user?.org_id, lifecycle]);

  const applyTemplate = (template) => {
    setMessageForm({
      ...messageForm,
      subject: template.subject,
      body: template.body
    });
    toast.success(`Template "${template.name}" applied`);
  };

  const closeComposer = () => {
    if ((messageForm.subject.trim() || messageForm.body.trim() || scheduledAt) && !window.confirm("Close this announcement? It has not been sent or saved as a template. Content stays here until you leave this page; closing clears the delivery schedule.")) return;
    setComposeOpen(false);
    setComposerExpanded(false);
    setPreviewMode(false);
    setScheduleMode(false);
    setScheduledAt("");
  };

  const handlePreview = () => {
    if (scheduleMode && scheduleTime(scheduledAt).error) {
      toast.error(scheduleTime(scheduledAt).error);
      return;
    }
    if (!messageForm.subject || !messageForm.body) {
      toast.error("Please fill in subject and message");
      return;
    }
    if (messageForm.type === "broadcast" && !messageForm.program_id) {
      toast.error("Please select a program");
      return;
    }
    if (messageForm.type === "individual" && !messageForm.household_id) {
      toast.error("Please select a household");
      return;
    }
    setPreviewMode(true);
  };

  const handleSendMessage = async () => {
    if (!ownsLifecycle()) return;
    setSending(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/admin/messages`, messageForm, { headers });
      if (!ownsLifecycle()) return;
      const status = response.data?.delivery?.status;
      toast.success(status === "accepted" || status === "delivered"
        ? `Delivery provider accepted ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}`
        : `Message queued for ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}`
      );
      trackAdminWorkflowCompleted("messaging");
      setComposeOpen(false);
      setComposerExpanded(false);
      setPreviewMode(false);
      setMessageForm({ type: "broadcast", program_id: "", household_id: "", subject: "", body: "" });
      fetchData();
    } catch (error) {
      if (!ownsLifecycle()) return;
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      if (ownsLifecycle()) setSending(false);
    }
  };

  // AI Email Generation
  const handleAiCompose = async () => {
    if (!ownsLifecycle()) return;
    if (!aiPrompt.trim()) {
      toast.error("Please describe what you want the email to say");
      return;
    }

    setAiLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get selected program info for context
      let programName = "";
      if (messageForm.program_id) {
        const program = programs.find(p => p.program_id === messageForm.program_id);
        programName = program?.name || "";
      }

      const response = await axios.post(`${API}/communications/ai/compose`, {
        prompt: aiPrompt,
        program_name: programName,
        tone: "friendly"
      }, { headers });

      if (!ownsLifecycle()) return;
      setMessageForm({
        ...messageForm,
        subject: response.data.subject || messageForm.subject,
        body: response.data.body_html || response.data.body || messageForm.body
      });

      setShowAiDialog(false);
      setAiPrompt("");
      
      if (response.data.generated) {
        toast.success("AI generated your email content!");
      } else {
        toast.info("Used template - AI was unavailable");
      }
    } catch (error) {
      if (!ownsLifecycle()) return;
      console.error("AI compose error:", error);
      toast.error("Failed to generate email content");
    } finally {
      if (ownsLifecycle()) setAiLoading(false);
    }
  };

  const handleAiCleanup = async () => {
    if (!ownsLifecycle()) return;
    if (!messageForm.body.trim()) {
      toast.error("Please write some content first");
      return;
    }

    setAiLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(`${API}/communications/ai/cleanup`, {
        content: messageForm.body,
        action: "improve"
      }, { headers });

      if (!ownsLifecycle()) return;
      setMessageForm({
        ...messageForm,
        body: response.data.improved_content || messageForm.body
      });

      toast.success("Email content improved!");
    } catch (error) {
      if (!ownsLifecycle()) return;
      console.error("AI cleanup error:", error);
      toast.error("Failed to clean up content");
    } finally {
      if (ownsLifecycle()) setAiLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!ownsLifecycle()) return;
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      toast.error("Please fill in all template fields");
      return;
    }
    
    setSavingTemplate(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingTemplate && editingTemplate.template_id) {
        await axios.put(`${API}/admin/email-templates/${editingTemplate.template_id}`, templateForm, { headers });
        if (!ownsLifecycle()) return;
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/admin/email-templates`, templateForm, { headers });
        if (!ownsLifecycle()) return;
        toast.success("Template saved");
      }
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      setTemplateForm({ name: "", category: "general", subject: "", body: "" });
      fetchData();
    } catch (error) {
      if (!ownsLifecycle()) return;
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    } finally {
      if (ownsLifecycle()) setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!ownsLifecycle()) return;
    if (!confirm("Are you sure you want to delete this template?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/email-templates/${templateId}`, { headers });
      if (!ownsLifecycle()) return;
      toast.success("Template deleted");
      fetchData();
    } catch (error) {
      if (!ownsLifecycle()) return;
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      category: template.category,
      subject: template.subject,
      body: template.body
    });
    setTemplateDialogOpen(true);
  };

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: "", category: "general", subject: "", body: "" });
    setTemplateDialogOpen(true);
  };

  const saveCurrentAsTemplate = () => {
    if (!messageForm.subject || !messageForm.body) {
      toast.error("Please fill in subject and message first");
      return;
    }
    setEditingTemplate(null);
    setTemplateForm({
      name: "",
      category: "general",
      subject: messageForm.subject,
      body: messageForm.body
    });
    setTemplateDialogOpen(true);
  };

  const getSelectedProgramName = () => {
    const program = programs.find(p => p.program_id === messageForm.program_id);
    return program?.name || "Unknown Program";
  };

  const getSelectedHouseholdName = () => {
    const household = households.find(h => h.household_id === messageForm.household_id);
    return household?.name || "Unknown Household";
  };

  const filteredMessages = messages.filter(msg =>
    searchTerm === "" ||
    msg.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTemplates = templates.filter(t =>
    templateFilter === "all" || t.category === templateFilter
  );

  const deliverySummary = getDeliverySummary(messages, scheduledEmails);
  const recoverableDeliveries = getRecoverableDeliveries(messages, scheduledEmails);
  const communicationActions = getCommunicationActions(messages, scheduledEmails);
  const audienceDescription = describeAudience(messageForm, programs, households);

  const recoverDeliveryAsDraft = (delivery) => {
    setMessageForm({
      type: delivery.type || "broadcast",
      program_id: delivery.program_id || "",
      household_id: delivery.household_id || "",
      subject: delivery.subject || "",
      body: delivery.body || "",
    });
    setPreviewMode(false);
    setScheduleMode(false);
    setComposeOpen(true);
    toast.info("Delivery reopened as a draft. Review the audience before sending.");
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-900"></div>
        </div>
      </PageLayout>
    );
  }

  if (loadError) {
    return (
      <PageLayout>
        <div className="mx-auto max-w-lg rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-red-700" />
          <h2 className="mt-3 font-semibold text-red-900">Messages could not be loaded</h2>
          <p className="mt-2 text-sm text-red-700">No delivery data was changed. Check your connection and try again.</p>
          <Button className="mt-4" variant="outline" onClick={() => { setLoading(true); fetchData(); }}>
            Try again
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="space-y-5 communication-workspace" data-testid="admin-communication-page">
        {!composeOpen && <>
        <EditorialPageHeader
          eyebrow="Communication operations / 06"
          title="Reach every family."
          description="Reach the right families, review timing, and recover delivery issues from one workspace."
          headingId="communication-title"
          aside={<div className="editorial-hero-metrics" aria-label="Delivery summary">
            <div><strong>{deliverySummary.queued}</strong><span>Queued</span></div>
            <div><strong>{deliverySummary.reached}</strong><span>Provider accepted</span></div>
            <div className={deliverySummary.failed ? "needs-attention" : ""}>
              <strong>{deliverySummary.failed}</strong><span>Needs review</span>
            </div>
          </div>}
        />

        {recoverableDeliveries.length > 0 && (
          <section className="communication-recovery" aria-label="Delivery recovery">
            <div>
              <AlertCircle className="w-4 h-4" />
              <span><strong>{recoverableDeliveries.length} delivery {recoverableDeliveries.length === 1 ? "needs" : "need"} review.</strong> Reopen the content as a draft and confirm the audience before trying again.</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => recoverDeliveryAsDraft(recoverableDeliveries[0])}>
              Review delivery
            </Button>
          </section>
        )}
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="communication-navigation">
            <TabsList className="communication-tabs bg-slate-100 rounded-2xl p-1">
              <TabsTrigger value="overview" className="rounded-xl data-[state=active]:bg-white">
                <Activity className="w-4 h-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="compose" className="rounded-xl data-[state=active]:bg-white">
                <Megaphone className="w-4 h-4 mr-2" />
                Announcements
              </TabsTrigger>
              <TabsTrigger value="scheduled" className="rounded-xl data-[state=active]:bg-white relative">
                <CalendarClock className="w-4 h-4 mr-2" />
                Delivery
                {scheduledEmails.length > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-violet-600 text-white rounded-full">
                    {scheduledEmails.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="analytics" className="rounded-xl data-[state=active]:bg-white">
                <BarChart3 className="w-4 h-4 mr-2" />
                Insights
              </TabsTrigger>
              <TabsTrigger value="templates" className="rounded-xl data-[state=active]:bg-white">
                <FileText className="w-4 h-4 mr-2" />
                Templates
              </TabsTrigger>
            </TabsList>
            
            {activeTab !== "templates" && (
              <Button
                onClick={() => { setComposeOpen(true); setPreviewMode(false); }}
                className="bg-[#123f3c] hover:bg-[#0b302e] rounded-2xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                New announcement
              </Button>
            )}
            {activeTab === "templates" && (
              <Button
                onClick={openNewTemplate}
                className="bg-slate-900 hover:bg-slate-800 rounded-2xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Template
              </Button>
            )}
          </div>

          <TabsContent value="overview" className="space-y-5 mt-5">
            <div className="communication-overview-grid">
              <section className="communication-panel" aria-labelledby="communication-actions-title">
                <div className="communication-panel-heading">
                  <div>
                    <p className="communication-panel-kicker">Operate today</p>
                    <h2 id="communication-actions-title">Action required</h2>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setActiveTab("scheduled")} className="rounded-xl">
                    Open delivery <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
                {communicationActions.length ? (
                  <div className="communication-action-list">
                    {communicationActions.map((action) => (
                      <button key={action.id} type="button" onClick={() => setActiveTab("scheduled")} className={`communication-action ${action.tone}`}>
                        <span>{action.count}</span><strong>{action.label}</strong><ArrowRight className="w-4 h-4" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="communication-clear-state">
                    <CheckCircle className="w-5 h-5" />
                    <div><strong>Nothing needs attention</strong><span>There are no failed or pending deliveries in the current history.</span></div>
                  </div>
                )}
              </section>

              <section className="communication-panel" aria-labelledby="communication-reach-title">
                <div className="communication-panel-heading">
                  <div>
                    <p className="communication-panel-kicker">Delivery truth</p>
                    <h2 id="communication-reach-title">Provider acceptance, not confirmed delivery</h2>
                  </div>
                  <ShieldCheck className="w-5 h-5 text-teal-700" />
                </div>
                <div className="communication-reach-grid">
                  <div><strong>{deliverySummary.reached}</strong><span>Accepted</span></div>
                  <div><strong>{deliverySummary.queued}</strong><span>Queued</span></div>
                  <div className={deliverySummary.failed ? "attention" : ""}><strong>{deliverySummary.failed}</strong><span>Review</span></div>
                </div>
                <p className="communication-panel-note">Recipient delivery records drive these states. Saving an announcement never counts as successful delivery.</p>
              </section>
            </div>

            <section className="communication-panel">
              <div className="communication-panel-heading">
                <div>
                  <p className="communication-panel-kicker">Recent communication</p>
                  <h2>Announcements and audience</h2>
                </div>
                <Button onClick={() => { setComposeOpen(true); setPreviewMode(false); }} className="rounded-xl bg-[#123f3c] hover:bg-[#0b302e]">
                  <Plus className="w-4 h-4 mr-2" /> New announcement
                </Button>
              </div>
              {messages.length ? (
                <div className="communication-recent-list">
                  {messages.slice(0, 5).map((message) => (
                    <div key={message.message_id}>
                      <div><strong>{message.subject}</strong><span>{message.audience_name || message.program_name || message.recipient_name || "Selected audience"}</span></div>
                      <span className={`communication-status ${message.status}`}>{describeMessageStatus(message.status)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="communication-empty-inline">Create the first announcement to begin a durable communication history.</div>
              )}
            </section>
          </TabsContent>

          <TabsContent value="compose" className="space-y-5 mt-5">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white border border-slate-200/60 rounded-2xl p-4">
                <p className="text-2xl font-semibold text-slate-900">{messages.length}</p>
                <p className="text-sm text-slate-500">Total Messages</p>
              </div>
              <div className="bg-white border border-slate-200/60 rounded-2xl p-4">
                <p className="text-2xl font-semibold text-slate-900">
                  {messages.filter(m => m.type === 'broadcast').length}
                </p>
                <p className="text-sm text-slate-500">Broadcasts</p>
              </div>
              <div className="bg-white border border-slate-200/60 rounded-2xl p-4">
                <p className="text-2xl font-semibold text-slate-900">
                  {messages.filter(m => ['sent', 'accepted', 'delivered'].includes(m.status)).length}
                </p>
                <p className="text-sm text-slate-500">Provider accepted</p>
              </div>
              <div className="bg-white border border-slate-200/60 rounded-2xl p-4">
                <p className="text-2xl font-semibold text-slate-900">{templates.length}</p>
                <p className="text-sm text-slate-500">Templates</p>
              </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-2xl border-slate-200"
              />
            </div>

            {/* Messages List */}
            <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h2 className="font-semibold text-slate-900">Message History</h2>
              </div>
              {filteredMessages.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {filteredMessages.map((message) => (
                    <div key={message.message_id} className="p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
                            message.type === 'broadcast' ? 'bg-violet-100' : 'bg-blue-100'
                          }`}>
                            {message.type === 'broadcast' ? (
                              <Users className="w-5 h-5 text-violet-600" />
                            ) : (
                              <User className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-900">{message.subject}</h3>
                            <p className="text-sm text-slate-500 mt-0.5">
                              {message.type === 'broadcast' ? (
                                <span className="flex items-center gap-1">
                                  <Trophy className="w-3.5 h-3.5" />
                                  {message.audience_name || message.program_name || "All registrants"}
                                  {message.recipients_count > 0 && (
                                    <span className="text-slate-400">• {message.recipients_count} recipients</span>
                                  )}
                                </span>
                              ) : (
                                <span>To: {message.recipient_name}</span>
                              )}
                            </p>
                            <p className="text-sm text-slate-400 mt-1 line-clamp-1">{message.body}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                            ['sent', 'accepted', 'delivered'].includes(message.status) ? 'bg-emerald-50 text-emerald-700' :
                            ['failed', 'partial', 'bounced', 'suppressed'].includes(message.status) ? 'bg-rose-50 text-rose-700' :
                            'bg-amber-50 text-amber-700'
                          }`}>
                            {['sent', 'accepted', 'delivered'].includes(message.status) ? <CheckCircle className="w-3 h-3" /> :
                             ['failed', 'partial', 'bounced', 'suppressed'].includes(message.status) ? <AlertCircle className="w-3 h-3" /> :
                             <Clock className="w-3 h-3" />}
                            {describeMessageStatus(message.status)}
                          </span>
                          <p className="text-xs text-slate-400 mt-1">
                            {message.created_at && format(new Date(message.created_at), "MMM d, h:mm a")}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Messages Yet</h3>
                  <p className="text-slate-500 mb-4">Start communicating with your registrants</p>
                  <Button onClick={() => setComposeOpen(true)} className="bg-slate-900 hover:bg-slate-800 rounded-2xl">
                    <Plus className="w-4 h-4 mr-2" />
                    Compose Message
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="scheduled" className="space-y-5 mt-5">
            <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-slate-900">Delivery queue & history</h2>
                  <p className="text-sm text-slate-500">Pending, sent, cancelled, and failed scheduled messages.</p>
                </div>
              </div>
              {scheduledLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : scheduledEmails.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {scheduledEmails.map((item) => (
                    <div key={item.schedule_id} className="p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-violet-100">
                            <CalendarClock className="w-5 h-5 text-violet-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-900">{item.subject}</h3>
                            <p className="text-sm text-slate-500 mt-0.5">
                              <Users className="w-3.5 h-3.5 inline mr-1" />
                              {item.recipients?.length || 0} recipient{(item.recipients?.length || 0) !== 1 ? "s" : ""}
                            </p>
                            <p className="text-sm text-slate-400 mt-1">
                              <Calendar className="w-3.5 h-3.5 inline mr-1" />
                              Scheduled for {item.scheduled_at ? format(new Date(item.scheduled_at), "MMM d, yyyy 'at' h:mm a") : "Unknown"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                            item.status === "pending" ? "bg-amber-50 text-amber-700" :
                            ["sent", "accepted", "delivered"].includes(item.status) ? "bg-emerald-50 text-emerald-700" :
                            ["failed", "partial", "bounced", "suppressed"].includes(item.status) ? "bg-rose-50 text-rose-700" :
                            "bg-slate-100 text-slate-500"
                          }`}>
                            {["pending", "queued", "deferred", "processing"].includes(item.status) ? <Clock className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                            {describeMessageStatus(item.status)}
                          </span>
                          {item.status === "pending" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelScheduled(item.schedule_id)}
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-xl h-8 w-8 p-0"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {["failed", "partial", "bounced", "suppressed"].includes(item.status) && (
                            <Button variant="outline" size="sm" onClick={() => recoverDeliveryAsDraft(item)} className="rounded-xl">
                              Review draft
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CalendarClock className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Scheduled Emails</h3>
                  <p className="text-slate-500">Schedule emails from the compose dialog</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-5 mt-5">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : analytics ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white border border-slate-200/60 rounded-2xl p-4">
                    <p className="text-2xl font-semibold text-slate-900">{analytics.summary.total_sent}</p>
                    <p className="text-sm text-slate-500">Total Sent</p>
                  </div>
                  <div className="bg-white border border-slate-200/60 rounded-2xl p-4">
                    <p className="text-2xl font-semibold text-emerald-600">{analytics.summary.open_rate}%</p>
                    <p className="text-sm text-slate-500">Open Rate</p>
                  </div>
                  <div className="bg-white border border-slate-200/60 rounded-2xl p-4">
                    <p className="text-2xl font-semibold text-blue-600">{analytics.summary.click_rate}%</p>
                    <p className="text-sm text-slate-500">Click Rate</p>
                  </div>
                  <div className="bg-white border border-slate-200/60 rounded-2xl p-4">
                    <p className="text-2xl font-semibold text-slate-900">{analytics.summary.unsubscribed}</p>
                    <p className="text-sm text-slate-500">Unsubscribed</p>
                  </div>
                </div>

                {/* Chart */}
                <div className="bg-white border border-slate-200/60 rounded-2xl p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Email Activity (Last 30 Days)</h3>
                  {analytics.daily && analytics.daily.length > 0 ? (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={analytics.daily}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(v) => v.slice(5)} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Area type="monotone" dataKey="sent" stroke="#6366f1" fill="#6366f1" fillOpacity={0.1} name="Sent" />
                          <Area type="monotone" dataKey="opened" stroke="#10b981" fill="#10b981" fillOpacity={0.1} name="Opened" />
                          <Area type="monotone" dataKey="clicked" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} name="Clicked" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400">
                      <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p>No email activity data yet</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No Analytics Data</h3>
                <p className="text-slate-500">Send some emails to start tracking analytics</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="templates" className="communication-templates space-y-5 mt-5">
            {/* Template Filter */}
            <div className="flex items-center gap-3">
              <Select value={templateFilter} onValueChange={setTemplateFilter}>
                <SelectTrigger aria-label="Filter templates" className="w-48 rounded-2xl border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Templates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id || template.template_id}
                  className="bg-white border border-slate-200/60 rounded-2xl p-5 hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-900">{template.name}</h3>
                      <span className="text-xs text-slate-500 capitalize">{template.category}</span>
                    </div>
                    <div className="flex gap-1">
                      {template.template_id && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditTemplate(template)}
                            aria-label={`Edit ${template.name}`}
                            className="rounded-xl h-8 w-8 p-0"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTemplate(template.template_id)}
                            aria-label={`Delete ${template.name}`}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-xl h-8 w-8 p-0"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 font-medium mb-2 truncate">{template.subject}</p>
                  <p className="text-sm text-slate-400 line-clamp-2 mb-4">{template.body}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { applyTemplate(template); setComposeOpen(true); setActiveTab("compose"); }}
                    className="w-full rounded-xl border-slate-200"
                  >
                    <Copy className="w-3.5 h-3.5 mr-2" />
                    Use Template
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
        </>}

        {/* Contained compose workspace */}
        {composeOpen && (
          <section ref={composerRef} className={`announcement-composer communication-athletic-composer${composerExpanded ? " announcement-composer--expanded" : ""}`} aria-label="Announcement composer" data-testid="announcement-composer-workspace">
            <div className="announcement-composer-inner">
              <header className="announcement-heading">
                <h1>{previewMode ? "Review announcement" : "New announcement"}</h1>
                <div className="announcement-actions">
                  <Button ref={expandRef} variant="outline" onClick={() => setComposerExpanded(!composerExpanded)} aria-expanded={composerExpanded}>{composerExpanded ? "Exit expanded view" : "Expand composer"}</Button>
                  <Button variant="ghost" size="icon" onClick={closeComposer} aria-label="Close announcement composer"><X className="h-5 w-5" /></Button>
                </div>
              </header>
              <div className="announcement-content">
            {!previewMode ? (
              <>
                <div className="announcement-grid">
                  <section className="announcement-audience" aria-labelledby="announcement-audience-label">
                    <h2 id="announcement-audience-label">Audience</h2>
                    <div className="announcement-type" aria-label="Audience type">
                      <Button variant="outline" aria-pressed={messageForm.type === "broadcast"} onClick={() => setMessageForm({ ...messageForm, type: "broadcast", household_id: "" })}><Users className="w-4 h-4" />Program</Button>
                      <Button variant="outline" aria-pressed={messageForm.type === "individual"} onClick={() => setMessageForm({ ...messageForm, type: "individual", program_id: "" })}><User className="w-4 h-4" />Household</Button>
                    </div>
                    {messageForm.type === "broadcast" ? (
                      <div className="announcement-field">
                        <Label htmlFor="announcement-program">Program</Label>
                        <Select value={messageForm.program_id} onValueChange={value => setMessageForm({ ...messageForm, program_id: value })}>
                          <SelectTrigger id="announcement-program"><SelectValue placeholder="Choose a program" /></SelectTrigger>
                          <SelectContent>{programs.map(program => <SelectItem key={program.program_id} value={program.program_id}>{program.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div className="announcement-field">
                        <Label htmlFor="announcement-household">Household</Label>
                        <Select value={messageForm.household_id} onValueChange={value => setMessageForm({ ...messageForm, household_id: value })}>
                          <SelectTrigger id="announcement-household"><SelectValue placeholder="Choose a household" /></SelectTrigger>
                          <SelectContent>{households.map(household => <SelectItem key={household.household_id} value={household.household_id}>{household.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="announcement-audience-status" role="status" aria-live="polite">
                      {audienceState === "idle" ? <p>Choose a {messageForm.type === "broadcast" ? "program" : "household"} to check recipients.</p> : audienceState === "loading" ? <p>Checking recipients…</p> : audienceState === "error" ? <><p>Recipients could not be checked.</p><Button variant="outline" size="sm" onClick={() => fetchAudiencePreview(messageForm)}>Retry audience check</Button></> : <>
                        <strong>{audienceDescription}</strong>
                        <p>{recipientCount} recipient{recipientCount !== 1 ? "s" : ""}</p>
                        {suppressedCount > 0 && <p>{suppressedCount} suppressed contact{suppressedCount === 1 ? "" : "s"} excluded</p>}
                        {recipientCount === 0 && <p>No eligible recipients in this audience.</p>}
                      </>}
                    </div>
                  </section>
                  <section className="announcement-message" aria-label="Message content">
                    <div className="announcement-tools">
                      <details><summary>Use a template</summary><div className="announcement-template-list">{templates.map(template => <Button key={template.id || template.template_id} variant="outline" size="sm" onClick={() => applyTemplate(template)}>{template.name}</Button>)}</div></details>
                      <details><summary>AI assistance</summary><div className="announcement-ai-actions">
                        <Button variant="outline" size="sm" onClick={() => setShowAiDialog(true)}><Sparkles className="w-4 h-4" />Write with AI</Button>
                        <Button variant="outline" size="sm" onClick={handleAiCleanup} disabled={aiLoading || !messageForm.body.trim()}><Wand2 className="w-4 h-4" />{aiLoading ? "Improving…" : "Clean up with AI"}</Button>
                      </div></details>
                    </div>
                    <div className="announcement-field">
                      <Label htmlFor="announcement-subject">Subject</Label>
                      <Input id="announcement-subject" value={messageForm.subject} onChange={e => setMessageForm({ ...messageForm, subject: e.target.value })} placeholder="Enter subject..." />
                    </div>
                    <div className="announcement-field">
                      <span id="announcement-message-label" className="text-sm font-medium">Message</span>
                      <MessageEditor>
                        <RichTextEditor value={messageForm.body} onChange={value => setMessageForm({ ...messageForm, body: value })} placeholder="Write your message..." minHeight="280px" />
                      </MessageEditor>
                      <details className="announcement-placeholders"><summary>Personalization placeholders</summary><p>{"{{parent_name}}, {{player_name}}, {{program_name}}, {{organization_name}}"}</p></details>
                    </div>
                  </section>
                  <section className="announcement-delivery" aria-labelledby="announcement-delivery-label">
                    <h2 id="announcement-delivery-label">Delivery</h2>
                    <p>Email · Preferences and provider rules apply.</p>
                    <Button type="button" variant="outline" aria-pressed={scheduleMode} onClick={() => { setScheduleMode(!scheduleMode); if (scheduleMode) setScheduledAt(""); }}><CalendarClock className="w-4 h-4" />{scheduleMode ? "Scheduling" : "Schedule"}</Button>
                    {scheduleMode && <div className="announcement-field"><Label htmlFor="announcement-scheduled-at">Send date and time</Label><Input id="announcement-scheduled-at" type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} aria-describedby="announcement-timezone announcement-time-error" aria-invalid={Boolean(scheduledAt && schedule.error)} />
                      <p id="announcement-timezone">Browser timezone: {schedule.zone}. Repeated fall-back times use the first occurrence. {schedule.utc && <>Selected offset: {schedule.offset}. UTC: {schedule.utc}.</>}</p>
                      <p id="announcement-time-error" role="alert">{scheduledAt && schedule.error}</p>
                    </div>}
                  </section>
                </div>
                <footer className="announcement-footer">
                  <Button variant="outline" onClick={saveCurrentAsTemplate}><Save className="w-4 h-4" />Save as template</Button>
                  <Button className="announcement-primary" onClick={handlePreview} disabled={audienceState !== "ready" || recipientCount === 0 || (scheduleMode && Boolean(schedule.error))}><Eye className="w-4 h-4" />Review announcement</Button>
                </footer>
              </>
            ) : (
              <>
                <div className="announcement-review-note">
                  <p>Confirm the audience and content before {scheduleMode ? "scheduling" : "sending"}.</p>
                  {scheduleMode && <p>Scheduled date and time: {scheduledAt.replace("T", " ")} · {schedule.zone} · {schedule.offset} · UTC: {schedule.utc}. Repeated fall-back times use the first occurrence.</p>}
                </div>

                <div className="py-4">
                  <div className="border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="bg-slate-50 p-4 border-b border-slate-200">
                      <div className="space-y-2 text-sm">
                        <div className="flex">
                          <span className="w-16 text-slate-500">From:</span>
                          <span className="text-slate-900 font-medium">{user?.org_name || "Your Organization"} &lt;noreply@sporthouse.ai&gt;</span>
                        </div>
                        <div className="flex">
                          <span className="w-16 text-slate-500">To:</span>
                          <span className="text-slate-900">
                            {messageForm.type === "broadcast" ? (
                              <>
                                <span className="font-medium">{getSelectedProgramName()}</span>
                                <span className="text-slate-500"> ({recipientCount} recipient{recipientCount !== 1 ? 's' : ''})</span>
                              </>
                            ) : (
                              <>
                                <span className="font-medium">{getSelectedHouseholdName()}</span>
                                {recipientEmails.length > 0 && (
                                  <span className="text-slate-500"> ({recipientEmails.join(", ")})</span>
                                )}
                              </>
                            )}
                          </span>
                        </div>
                        <div className="flex">
                          <span className="w-16 text-slate-500">Subject:</span>
                          <span className="text-slate-900 font-medium">{messageForm.subject}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6 bg-white">
                      <div className="prose prose-slate max-w-none">
                        <ProgramDescription value={messageForm.body} />
                      </div>
                      <div className="announcement-review-footer mt-8 pt-4 border-t border-slate-100 text-xs">
                        <p>This email was sent by {user?.org_name || "Sporthouse"}</p>
                      </div>
                    </div>
                  </div>

                  {recipientEmails.length > 0 && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-2xl">
                      <h4 className="text-sm font-medium text-slate-700 mb-2">
                        {messageForm.type === "broadcast" ? "Sample Recipients:" : "Recipients:"}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {recipientEmails.slice(0, 5).map((email, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs text-slate-600">
                            <Mail className="w-3 h-3" />
                            {email}
                          </span>
                        ))}
                        {recipientCount > 5 && (
                          <span className="px-2 py-1 text-xs text-slate-500">
                            +{recipientCount - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <footer className="announcement-footer">
                  <Button
                    variant="outline"
                    onClick={() => setPreviewMode(false)}
                    className="rounded-2xl border-slate-200"
                  >
                    Edit Message
                  </Button>
                  <Button
                    onClick={scheduleMode ? handleScheduleEmail : handleSendMessage}
                    disabled={sending || audienceState !== "ready" || recipientCount === 0 || (scheduleMode && !scheduledAt)}
                    className="announcement-primary"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {scheduleMode ? "Schedule Send" : `Send to ${recipientCount} Recipient${recipientCount !== 1 ? "s" : ""}`}
                      </>
                    )}
                  </Button>
                </footer>
              </>
            )}
              </div>
            </div>
          </section>
        )}

        {/* Template Dialog */}
        <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
          <DialogContent className="max-w-lg rounded-2xl">
            <DialogHeader>
              <DialogTitle>{editingTemplate ? "Edit Template" : "Save as Template"}</DialogTitle>
              <DialogDescription>
                Save this message format for future use
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="communication-template-name">Template Name</Label>
                <Input
                  id="communication-template-name"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Weekly Practice Reminder"
                  className="rounded-2xl border-slate-200"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="communication-template-category">Category</Label>
                <Select
                  value={templateForm.category}
                  onValueChange={(value) => setTemplateForm({ ...templateForm, category: value })}
                >
                  <SelectTrigger id="communication-template-category" className="rounded-2xl border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="scheduling">Scheduling</SelectItem>
                    <SelectItem value="financial">Financial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="communication-template-subject">Subject</Label>
                <Input
                  id="communication-template-subject"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="Email subject line"
                  className="rounded-2xl border-slate-200"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="communication-template-body">Message Body</Label>
                <Textarea
                  id="communication-template-body"
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                  placeholder="Template message content..."
                  rows={6}
                  className="rounded-2xl border-slate-200 resize-none"
                />
                <p className="text-xs text-slate-500">
                  Tip: Use placeholders like {"{{program_name}}"}, {"{{player_name}}"}, {"{{parent_name}}"}, {"{{organization_name}}"}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setTemplateDialogOpen(false)}
                className="rounded-2xl border-slate-200"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveTemplate}
                disabled={savingTemplate}
                className="bg-slate-900 hover:bg-slate-800 rounded-2xl"
              >
                {savingTemplate ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Template
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Compose Dialog */}
        <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Write with AI
              </DialogTitle>
              <DialogDescription>
                Describe what you want your email to say
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>What should this email say?</Label>
                <Textarea
                  placeholder="e.g., Remind parents about the upcoming practice on Saturday, ask them to bring water bottles and arrive 15 minutes early..."
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  rows={4}
                  className="rounded-2xl border-slate-200"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowAiDialog(false)}
                className="rounded-2xl border-slate-200"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAiCompose}
                disabled={aiLoading || !aiPrompt.trim()}
                className="bg-purple-600 hover:bg-purple-700 rounded-2xl gap-2"
              >
                {aiLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Generate Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageLayout>
  );
};

export default AdminMessaging;
